import { Outlet, createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { FlexiblePanelLayout } from '@/components/FlexiblePanelLayout/FlexiblePanelLayout';
import { LeftMainHeader, Tab } from '@/components/FlexiblePanelLayout/LeftMainHeader';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { LayoutHeaderTitle } from '@/components/LayoutHeaderTitle';
import { NotFound404 } from '@/components/NotFound404';
import { useSplatPathSegments } from '@/hooks/useSplatPathSegments';
import { useUser } from '@/hooks/useUser';

export const Route = createFileRoute('/$organizationName/settings')({
  component: SettingsLayout,
  notFoundComponent: () => <NotFound404 />,
});

interface SettingsTab extends Tab {
  order: number;
}

// Segments that resolve under the Access Control parent layout. When the
// user is on /settings/users, /teams, or /sources, the active first-tier tab
// is 'access' — the shared header + PillNav comes from settings/_access.tsx.
const accessChildSegments = ['users', 'teams', 'sources'];

function SettingsLayout() {
  const { isSourceAdmin, isSuperAdmin, routeOrganizationName } = useUser();
  const pathSegments = useSplatPathSegments(Route);
  const rawSegment = pathSegments[0]?.toLowerCase();
  // Path-prefix matching: if the user is on /settings/users, /teams, or /sources,
  // the active first-tier tab is the Access Control parent.
  const activeChild = accessChildSegments.includes(rawSegment ?? '') ? 'access' : rawSegment;

  // Scroll the main panel back to the top when the user switches between
  // first-tier Settings tabs. activeChild is the first-tier key, so it does
  // NOT change when the user clicks an Access Control PillNav child
  // (sources / teams / users all map to activeChild === 'access') or opens /
  // navigates inside a drawer — both of those preserve scroll. Scoped here so
  // other FlexiblePanelLayout consumers (codebase view, PDF view) aren't
  // affected.
  const previousActiveChild = useRef(activeChild);
  useEffect(() => {
    if (activeChild === previousActiveChild.current) return;
    previousActiveChild.current = activeChild;
    document.getElementById('main-panel')?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeChild]);

  // Where the Access Control first-tier tab navigates when clicked. Land on
  // the first visible child in the displayed order (Sources → Teams → Users),
  // so whoever can see Sources lands there; otherwise everyone lands on Teams
  // (always visible).
  const firstAccessChild: '/$organizationName/settings/sources' | '/$organizationName/settings/teams' =
    isSuperAdmin || isSourceAdmin ? '/$organizationName/settings/sources' : '/$organizationName/settings/teams';

  const tocItems: SettingsTab[] = [
    {
      order: 1,
      key: 'organization',
      displayName: 'Organization',
      to: `/$organizationName/settings/organization`,
      params: { organizationName: routeOrganizationName },
    },
    {
      order: 2,
      key: 'account',
      displayName: 'Account',
      to: `/$organizationName/settings/account`,
      params: { organizationName: routeOrganizationName },
    },
    {
      order: 3,
      key: 'access',
      displayName: 'Access Control',
      to: firstAccessChild,
      params: { organizationName: routeOrganizationName },
    },
    {
      order: 4,
      key: 'access-tokens',
      displayName: 'Access Tokens',
      to: `/$organizationName/settings/access-tokens`,
      params: { organizationName: routeOrganizationName },
    },
  ];

  if (isSuperAdmin) {
    tocItems.push({
      order: 5,
      key: 'billing',
      displayName: 'Billing',
      to: `/$organizationName/settings/billing`,
      params: { organizationName: routeOrganizationName },
    });
    tocItems.push({
      order: 6,
      key: 'integrations',
      displayName: 'Integrations',
      to: `/$organizationName/settings/integrations`,
      params: { organizationName: routeOrganizationName },
    });
    tocItems.push({
      order: 7,
      key: 'byom',
      displayName: 'Models',
      to: `/$organizationName/settings/byom`,
      params: { organizationName: routeOrganizationName },
    });
    tocItems.push({
      order: 8,
      key: 'labs',
      displayName: 'Labs',
      to: `/$organizationName/settings/labs`,
      params: { organizationName: routeOrganizationName },
    });
  }

  const sortedTocItems = tocItems.sort((a, b) => a.order - b.order);

  return (
    <LayoutDefault
      navbarTitle="Settings"
      header={
        <LayoutHeader>
          <LayoutHeaderTitle title="Settings" />
        </LayoutHeader>
      }
    >
      <FlexiblePanelLayout
        showBorder
        pageType="settings"
        // Compensate for the navbar height to avoid double scrollbars.
        className="h-[calc(100vh-44px)]"
        mainPanelContent={<Outlet />}
        leftMainHeader={<LeftMainHeader selectedTab={activeChild} tabs={sortedTocItems} />}
      />
    </LayoutDefault>
  );
}
